import React from 'react';
import { PayoutLadder, GameSession, GameRound } from '../../services/GameApiService';

interface ProgressionLadderProps {
  payoutLadder: PayoutLadder | null;
  currentSession: GameSession | null;
  currentRound: GameRound | null;
  winLossStatus?: 'winning' | 'losing' | 'won' | 'lost' | 'cashed_out' | null;
  betAmount?: number;
  // Share boost props for Level 2 multiplier boost
  hasBoost?: boolean; // Boost applies to current session (unused OR used in this session)
  boostValue?: number; // e.g., 1.5 for 50% boost
}

const ProgressionLadder: React.FC<ProgressionLadderProps> = ({
  payoutLadder,
  currentSession,
  currentRound,
  winLossStatus,
  betAmount = 0.01,
  hasBoost = false,
  boostValue = 1.5
}) => {
  // If no payout ladder yet, show loading state
  if (!payoutLadder || !payoutLadder.ladder) {
    return (
      <div className="progression-ladder inactive">
        <div className="ladder-loading">Loading ladder...</div>
      </div>
    );
  }

  const { ladder } = payoutLadder;
  const roundsCompleted = currentSession?.rounds_completed || 0;
  const initialBet = currentSession?.initial_bet || 0;
  
  // Helper to get multiplier with boost applied for Level 2
  const getMultiplier = (ladderIndex: number): number => {
    const baseMultiplier = ladder[ladderIndex]?.multiplier || 0;
    // Apply boost only to Level 2 (ladder index 1) if boost applies to current session
    if (ladderIndex === 1 && hasBoost) {
      return parseFloat((baseMultiplier * boostValue).toFixed(2));
    }
    return baseMultiplier;
  };
  
  // Determine if session is active
  const isActive = currentSession?.session_status === 'active';
  const sessionStatus = currentSession?.session_status;
  const currentPhase = currentRound?.phase;
  const playerWon = currentRound?.player_won;
  
  // Determine which UI phase we're in
  // Game phases: BETTING → RACING → SETTLEMENT → RESULTS
  // UI phases: START, RACING, DECISION
  //
  // START: No session or game over → single target box (Level 1)
  // RACING: BETTING, RACING, SETTLEMENT → single target box (what you're racing for)
  // DECISION: RESULTS + won → two boxes (secured ✓ → next target)
  
  let phase: 'START' | 'DECISION' | 'RACING';
  
  if (!isActive || sessionStatus === 'busted' || sessionStatus === 'cashed_out') {
    phase = 'START';
  } else if (currentPhase === 'RESULTS' && playerWon === true) {
    phase = 'DECISION';  // Only RESULTS + won shows both boxes
  } else {
    phase = 'RACING';  // BETTING, RACING, SETTLEMENT all show single target
  }
  
  // Calculate relevant bet
  const relevantBet = isActive && initialBet > 0 ? initialBet : betAmount;
  
  // Level and multiplier logic depends on phase:
  // Backend sets: rounds_completed = roundNumber when player wins
  // So rounds_completed = 1 means they completed round 1 (won ladder[0])
  //
  // START: rounds_completed = 0, showing Level 1 (ladder[0])
  // DECISION: just won a race
  //   - rounds_completed = 1 → just won round 1 → secured ladder[0] (1.9x) → next is ladder[1] (3.6x)
  //   - rounds_completed = N → just won round N → secured ladder[N-1] → next is ladder[N]
  // RACING: actively racing
  //   - rounds_completed = 1 → racing for round 2 → targeting ladder[1] (3.6x)
  //   - rounds_completed = N → racing for round N+1 → targeting ladder[N]
  
  let currentLevel: number;
  let securedMultiplier: number = 0;
  let securedPayout: number = 0;
  let targetMultiplier: number = 0;
  let targetPayout: number = 0;
  
  if (phase === 'START') {
    // Starting fresh - race for Level 1 (round 1, ladder[0])
    currentLevel = 1;
    targetMultiplier = getMultiplier(0);
    targetPayout = relevantBet * targetMultiplier;
  } else if (phase === 'DECISION') {
    // Just won a race - show what they secured + what's NEXT
    // rounds_completed = N means they just won round N (ladder[N-1])
    // Show NEXT level (N+1) they're deciding to race for
    currentLevel = roundsCompleted + 1; // Show the NEXT level they're deciding on
    securedMultiplier = getMultiplier(roundsCompleted - 1);
    securedPayout = relevantBet * securedMultiplier;
    targetMultiplier = getMultiplier(roundsCompleted); // This will apply boost for Level 2
    targetPayout = relevantBet * targetMultiplier;
  } else {
    // RACING - racing for next level
    // rounds_completed = N means they're racing for round N+1 (ladder[N])
    currentLevel = roundsCompleted + 1;
    targetMultiplier = getMultiplier(roundsCompleted); // This will apply boost for Level 2
    targetPayout = relevantBet * targetMultiplier;
  }
  
  // Format multiplier: whole numbers for >= 10x, 1 decimal for < 10x
  const formatMultiplier = (mult: number): string => {
    if (mult >= 10) {
      return `${Math.floor(mult)}x`;
    }
    const formatted = mult.toFixed(1).replace(/\.0$/, '');
    return `${formatted}x`;
  };
  
  // Format payout (SOL amount)
  const formatPayout = (payout: number): string => {
    return payout >= 1 ? payout.toFixed(2) : payout.toFixed(3);
  };

  return (
    <div className={`progression-ladder phase-${phase.toLowerCase()} ${isActive ? 'active' : 'inactive'}`}>
      {/* Header: MOON RUNNER branding */}
      <div className="ladder-header">
        <img 
          src="/assets/ui/moonrunner-logo.svg" 
          alt="MOON RUNNER" 
          className="ladder-logo"
        />
        <div className="ladder-subtitle">win races. launch tokens. run it up</div>
      </div>
      
      {/* Main content area - changes per phase */}
      <div className="ladder-content">
        {phase === 'START' && (
          // PHASE 1: Single centered box - race for first multiplier
          <div className="multiplier-display single">
            <div className="multiplier-box target">
              <div className="token-value-label">EST. TOKEN VALUE</div>
              <div className="multiplier-value">
                <img 
                  src="/assets/icons/solanaLogoMark.svg" 
                  alt="SOL" 
                  className="payout-sol-icon"
                />
                {formatPayout(targetPayout)}
              </div>
              <div className="payout-value">
                {formatMultiplier(targetMultiplier)} <span className="level-marker">RUN.{currentLevel}</span>
              </div>
            </div>
          </div>
        )}
        
        {phase === 'DECISION' && (
          // PHASE 2: Two boxes - secured (what they won) + target (what's next)
          <div className="multiplier-display decision">
            <div className="multiplier-box secured">
              <div className="token-value-label">EST. TOKEN VALUE</div>
              <div className="multiplier-value">
                <img 
                  src="/assets/icons/solanaLogoMark.svg" 
                  alt="SOL" 
                  className="payout-sol-icon"
                />
                {formatPayout(securedPayout)}
              </div>
              <div className="payout-value">
                {formatMultiplier(securedMultiplier)} <span className="level-marker">RUN.{currentLevel - 1}</span>
              </div>
            </div>
            <div className="arrow">→</div>
            <div className="multiplier-box target">
              <div className="token-value-label">EST. TOKEN VALUE</div>
              <div className="multiplier-value">
                <img 
                  src="/assets/icons/solanaLogoMark.svg" 
                  alt="SOL" 
                  className="payout-sol-icon"
                />
                {formatPayout(targetPayout)}
              </div>
              <div className="payout-value">
                {formatMultiplier(targetMultiplier)} <span className="level-marker">RUN.{currentLevel}</span>
              </div>
            </div>
          </div>
        )}
        
        {phase === 'RACING' && (
          // PHASE 3: Single centered box - racing for target
          <div className="multiplier-display single">
            <div className="multiplier-box target racing">
              <div className="token-value-label">EST. TOKEN VALUE</div>
              <div className="multiplier-value">
                <img 
                  src="/assets/icons/solanaLogoMark.svg" 
                  alt="SOL" 
                  className="payout-sol-icon"
                />
                {formatPayout(targetPayout)}
              </div>
              <div className="payout-value">
                {formatMultiplier(targetMultiplier)} <span className="level-marker">RUN.{currentLevel}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressionLadder;
